import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ItemMaster from "@/pages/masters/ItemMaster";
import VendorMaster from "@/pages/masters/VendorMaster";
import CustomerMaster from "@/pages/masters/CustomerMaster";
import RMReceipt from "@/pages/transactions/RMReceipt";
import CuttingProduction from "@/pages/transactions/CuttingProduction";
import StageIssue from "@/pages/transactions/StageIssue";
import StageReceipt from "@/pages/transactions/StageReceipt";
import PartConversion from "@/pages/transactions/PartConversion";
import DispatchEntry from "@/pages/transactions/DispatchEntry";
import RMStock from "@/pages/inventory/RMStock";
import StageStock from "@/pages/inventory/StageStock";
import RMOrders from "@/pages/inventory/RMOrders";
import VendorWIP from "@/pages/inventory/VendorWIP";
import PendingStock from "@/pages/inventory/PendingStock";
import OpeningStock from "@/pages/inventory/OpeningStock";
import DetailedStockSheet from "@/pages/inventory/DetailedStockSheet";
import ScheduleManagement from "@/pages/schedule/ScheduleManagement";
import ScheduleVsDispatch from "@/pages/schedule/ScheduleVsDispatch";
import DispatchPlan from "@/pages/schedule/DispatchPlan";
import ScheduleUpload from "@/pages/schedule/ScheduleUpload";
import UserManagement from "@/pages/UserManagement";
import PartTraceability from "@/pages/reports/PartTraceability";
import RMConsumption from "@/pages/reports/RMConsumption";
import RMRequirement from "@/pages/reports/RMRequirement";
import StageAgeing from "@/pages/reports/StageAgeing";
import MonthlyReport from "@/pages/reports/MonthlyReport";
import WeeklyTarget from "@/pages/reports/WeeklyTarget";
import NegativeStockAudit from "@/pages/admin/NegativeStockAudit";
import DataManagement from "@/pages/admin/DataManagement";
import Challans from "@/pages/transactions/Challans";
import RejectionEntry from "@/pages/transactions/RejectionEntry";
import SaleReturnEntry from "@/pages/transactions/SaleReturnEntry";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

// Hybrid auth: httpOnly cookie (primary) + Bearer header (fallback for Safari/ITP)
axios.defaults.withCredentials = true;

// Request interceptor: attach Bearer token from localStorage as fallback
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail || "";
      const isTokenError = typeof detail === "string" && (detail.includes("expired") || detail.includes("Invalid token") || detail.includes("Not authenticated"));
      if (isTokenError && localStorage.getItem("user") && !window.location.pathname.includes("/login")) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        toast.error("Session expired. Please login again.");
        window.location.href = "/login";
      }
    }
    if (error.response?.status === 403) {
      toast.error(error.response?.data?.detail || "You don't have permission for this action");
    }
    return Promise.reject(error);
  }
);

function ProtectedRoute({ children }) {
  const user = localStorage.getItem("user");
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/masters/items" element={<ItemMaster />} />
                    <Route path="/masters/vendors" element={<VendorMaster />} />
                    <Route path="/masters/customers" element={<CustomerMaster />} />
                    <Route path="/transactions/rm-receipt" element={<RMReceipt />} />
                    <Route path="/transactions/cutting" element={<CuttingProduction />} />
                    <Route path="/transactions/stage-issue" element={<StageIssue />} />
                    <Route path="/transactions/stage-receipt" element={<StageReceipt />} />
                    <Route path="/transactions/conversion" element={<PartConversion />} />
                    <Route path="/transactions/dispatch" element={<DispatchEntry />} />
                    <Route path="/transactions/challans" element={<Challans />} />
                    <Route path="/transactions/rejection" element={<RejectionEntry />} />
                    <Route path="/transactions/sale-return" element={<SaleReturnEntry />} />
                    <Route path="/inventory/rm-stock" element={<RMStock />} />
                    <Route path="/inventory/stage-stock" element={<StageStock />} />
                    <Route path="/inventory/detailed-stock" element={<DetailedStockSheet />} />
                    <Route path="/inventory/rm-orders" element={<RMOrders />} />
                    <Route path="/inventory/vendor-wip" element={<VendorWIP />} />
                    <Route path="/inventory/pending" element={<PendingStock />} />
                    <Route path="/inventory/opening-stock" element={<OpeningStock />} />
                    <Route path="/schedule/manage" element={<ScheduleManagement />} />
                    <Route path="/schedule/vs-dispatch" element={<ScheduleVsDispatch />} />
                    <Route path="/schedule/dispatch-plan" element={<DispatchPlan />} />
                    <Route path="/schedule/upload" element={<ScheduleUpload />} />
                    <Route path="/reports/part-traceability" element={<PartTraceability />} />
                    <Route path="/reports/rm-consumption" element={<RMConsumption />} />
                    <Route path="/reports/rm-requirement" element={<RMRequirement />} />
                    <Route path="/reports/stage-ageing" element={<StageAgeing />} />
                    <Route path="/reports/monthly-report" element={<MonthlyReport />} />
                    <Route path="/reports/weekly-target" element={<WeeklyTarget />} />
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/admin/negative-stock-audit" element={<NegativeStockAudit />} />
                    <Route path="/admin/data-management" element={<DataManagement />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;